import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { clearAuth, setAuthData, setProfile, setSessions, setTotpEnabled, setFtpData, setAuthenticated, setToken } from "@/store/slices/authSlice";
import { profileApi } from "@/api/profile";
import { isCmsUrl, appendTokenToCmsRedirect } from "@/utils/redirectHelpers";
import { checkRedirectPermissions } from "@/utils/redirectPermissionCheck";
import { recordRedirectAttempt, LOOP_REDIRECT_QUERY_PARAM } from "@/utils/redirectLoopGuard";

const PUBLIC_ROUTES = ['/', '/login', '/request-access', '/verify-otp'];
const LOGIN_ROUTES = ['/', '/login'];

const handleExternalRedirect = (
  search: string, 
  token: string | null, 
  navigate: ReturnType<typeof useNavigate>,
  userPermissions: string[] = []
) => {
  const params = new URLSearchParams(search);
  const redirectParam = params.get("redirect") || params.get("returnUrl");
  
  if (redirectParam && redirectParam.startsWith('http')) {
    const { hasAccess } = checkRedirectPermissions(redirectParam, userPermissions);

    if (!hasAccess) {
      console.log('AuthGuard: User lacks access to redirect target, navigating to /home with redirect param');
      navigate(`/home?redirect=${encodeURIComponent(redirectParam)}`, { replace: true });
      return true;
    }

    const { isLooping, origin } = recordRedirectAttempt(redirectParam);
    if (isLooping) {
      navigate(`/home?${LOOP_REDIRECT_QUERY_PARAM}=${encodeURIComponent(origin || redirectParam)}`, { replace: true });
      return true;
    }

    const redirectUrl = isCmsUrl(redirectParam) && token
      ? appendTokenToCmsRedirect(redirectParam, token)
      : redirectParam;
    window.location.href = redirectUrl;
    return true;
  }
  navigate("/home", { replace: true });
  return false;
};

export const AuthGuard = ({ children }: { children: React.ReactNode }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, token, profile } = useAppSelector((state) => state.auth);
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);
  const hasCheckedAuth = useRef<string | false>(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isPublicRoute = PUBLIC_ROUTES.includes(location.pathname);
  const isLoginRoute = LOGIN_ROUTES.includes(location.pathname);

  useEffect(() => {
    // Cancel any pending API request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Skip if we have auth state
    if (isAuthenticated) {
      if (isLoginRoute) {
        const userPermissions = profile?.permissions || [];
        handleExternalRedirect(location.search, token, navigate, userPermissions);
      }
      hasCheckedAuth.current = false;
      return;
    }

    // Skip API check for other public routes (not login routes)
    if (isPublicRoute && !isLoginRoute) {
      hasCheckedAuth.current = false; // Reset so we can check when navigating to protected routes
      return;
    }

    // Use a route-specific key to allow checking again when route changes
    const routeKey = location.pathname;
    const lastCheckedRoute = hasCheckedAuth.current;
    
    // Skip if we already checked this exact route
    if (lastCheckedRoute === routeKey) {
      return;
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    // Set checking state BEFORE calling the async function
    hasCheckedAuth.current = routeKey; // Store route key instead of boolean
    setIsCheckingAuth(true);

    // Check API to verify if user has valid session via cookies
    const checkAuthFromAPI = async () => {

      try {
        const { profile, sessions, totpEnabled, ftpData, token: apiToken } = await profileApi.getUserData();

        // Check if request was aborted
        if (abortController.signal.aborted) {
          return;
        }

        // Check if we got valid user data (has id or email)
        if (profile && (profile.id || profile.email)) {
          // Set token first if API returned one
          if (apiToken) {
            dispatch(setToken(apiToken));
          }
          
          // Restore auth state from API response
          dispatch(setAuthenticated(true));
          dispatch(setAuthData({
            email: profile.email,
            name: profile.name,
            profile: profile,
            sessions: sessions,
          }));
          dispatch(setProfile(profile));
          dispatch(setSessions(sessions));
          dispatch(setTotpEnabled(totpEnabled));
          if (ftpData) {
            dispatch(setFtpData(ftpData));
          }
          
          // If on login route, check for external redirect before navigating
          // Use the token from API response (which we just set in Redux)
          if (isLoginRoute) {
            const userPermissions = profile.permissions || [];
            handleExternalRedirect(location.search, apiToken || token, navigate, userPermissions);
          }
        } else {
          // No valid user data
          // Only redirect if on protected route (not login route)
          if (!isLoginRoute) {
            dispatch(clearAuth());
            navigate("/", { replace: true });
          } else {
            // On login route, just clear auth state but stay on page
            dispatch(clearAuth());
          }
        }
      } catch (error) {
        // Check if request was aborted (don't log or handle aborted requests)
        if (abortController.signal.aborted) {
          return;
        }

        // API call failed (401/403 or network error)
        console.error('Auth check failed:', error);
        // Clear any stale auth state
        dispatch(clearAuth());
        // Only redirect if on protected route (not login route)
        if (!isLoginRoute) {
          navigate("/", { replace: true });
        }
      } finally {
        // Only update state if this request wasn't aborted
        if (!abortController.signal.aborted) {
          setIsCheckingAuth(false);
        }
        // Clear abort controller reference if it's the current one
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
        }
      }
    };

    checkAuthFromAPI();

    // Cleanup: abort request if component unmounts or effect re-runs
    return () => {
      if (abortControllerRef.current === abortController) {
        abortController.abort();
        abortControllerRef.current = null;
      }
    };
  }, [isAuthenticated, token, isPublicRoute, isLoginRoute, location.pathname, location.search, dispatch, navigate]);

  useEffect(() => {
    // Don't redirect if we're checking auth OR if we haven't checked yet
    // hasCheckedAuth.current will be set to the route key when we start checking
    const isCurrentlyChecking = isCheckingAuth || hasCheckedAuth.current === location.pathname;
    
    if (!isAuthenticated && !isPublicRoute && !isCurrentlyChecking) {
      navigate("/", { replace: true });
      return;
    }

    const handleUnauthorized = () => {
      if (isPublicRoute) {
        return;
      }

      if (isAuthenticated) {
        dispatch(clearAuth());
        navigate("/", { replace: true });
      }
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized as EventListener);
    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized as EventListener);
    };
  }, [isAuthenticated, isPublicRoute, location.pathname, dispatch, navigate, isCheckingAuth]);
  
  // Show nothing while checking auth from API
  if (isCheckingAuth) {
    return null;
  }

  if (!isAuthenticated && !isPublicRoute) {
    return null;
  }

  return <>{children}</>;
};
